import {
  CartDetails,
  getUpdatedPhysicalProducts,
} from "../../lib/cart/CartDetails";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import { useCallback, useContext, useMemo } from "react";

import AddToCartButton from "../../components/shop/AddToCartButton";
import ArrowRightO from "../../svg/arrow-right-o.svg";
import Button from "../../components/Button";
import { CartContext } from "../../contexts/CartContext";
import Content from "../../content";
import ContentfulClient from "../../lib/contentful_client/ContentfulClient";
import FoundryLink from "../../components/FoundryLink";
import FoundryPhysicalProduct from "../../lib/foundry_client/models/FoundryPhysicalProduct";
import Head from "next/head";
import Heart from "../../svg/heart.svg";
import { ImageDisplayMode } from "../../lib/models/ImageDisplayMode";
import Info from "../../svg/info.svg";
import Loader from "../../components/Loader";
import { PageFadeWrapper } from "../../components/PageWrapper";
import ProductCard from "../../components/shop/ProductCard";
import ResponsiveImage from "../../components/ResponsiveImage";
import Routes from "../../routes";
import { formatMoney } from "../../util/strings";
import { savePhysicalProducts } from "../../lib/cart/CartStorage";
import { toast } from "react-toastify";
import useEmblaCarousel from "embla-carousel-react";
import { useParams } from "next/navigation";
import usePhysicalProducts from "../../hooks/usePhysicalProducts";
import useQuantitySelect from "../../hooks/useQuantitySelect";

function Product() {
  const { cartDetails, propagateCartDetails } = useContext(CartContext);
  const [emblaRef, emblaApi] = useEmblaCarousel();

  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);

  const params = useParams<{ slug: string }>();
  const physicalProducts = usePhysicalProducts();
  const [quantitySelector, quantity, setMaxQuantity] = useQuantitySelect(0);

  const { product, otherProducts, notFound } = useMemo(() => {
    if (physicalProducts.length === 0) {
      return {
        product: null,
        otherProducts: null,
        notFound: false,
      };
    }
    const productIdx = physicalProducts.findIndex(
      (p) => p.slug === params.slug
    );
    if (productIdx === -1) {
      return {
        product: null,
        otherProducts: null,
        notFound: true,
      };
    }
    const product = physicalProducts[productIdx];
    const newProducts = [...physicalProducts];
    newProducts.splice(productIdx, 1);
    const shuffled = newProducts
      .filter((p) => p.maxQuantity > p.orderedQuantity)
      .sort(() => 0.5 - Math.random());

    const availableQuantity = product.maxQuantity - product.orderedQuantity;
    const availableAndNotInCart =
      availableQuantity -
      (cartDetails?.physicalProducts?.find((p) => p.contentfulId === product.id)
        ?.quantity || 0);
    setMaxQuantity(availableAndNotInCart);
    return {
      product,
      otherProducts: shuffled.slice(0, 3),
      notFound: false,
    };
  }, [physicalProducts]);

  if (notFound) {
    return (
      <>
        <Head>
          <title>Page Not Found | {Content.siteName}</title>
        </Head>
        <PageFadeWrapper>
          <main className="flex justify-center items-center min-w-full min-h-screen">
            <p>We couldn't find a page at this address.</p>
          </main>
        </PageFadeWrapper>
      </>
    );
  }

  if (product === null) {
    return (
      <div className="w-full pt-48 flex items-center justify-center">
        <Loader size={90} />
      </div>
    );
  }

  console.log({ product });

  const availableQuantity = product.maxQuantity - product.orderedQuantity;

  const isMasonryMode =
    product.imageDisplayMode === ImageDisplayMode.MASONRY_ON_LARGE_SCREENS;

  const imgSection = product.images.map((image) => {
    return (
      <div className="embla__slide">
        <ResponsiveImage
          src={image.fileUrl}
          alt={image.description}
          sizes="(max-width: 768px) 45vw, (max-width: 1536px) 20vw, 250px"
        ></ResponsiveImage>
      </div>
    );
  });

  let embla = "embla";
  let emblaContainer = "embla__container";
  let emblaSlide = "embla__slide";

  if (!isMasonryMode) {
    embla += "--always";
    emblaContainer += "--always";
    emblaSlide += "--always";
  }

  return (
    <>
      <Head>
        <title>
          {product.name} | {Content.siteName}
        </title>
      </Head>
      <PageFadeWrapper>
        <div className="nav-spacer container-wide pt-12 pb-24 lg:pb-36 xl:pb-48">
          <span>
            <FoundryLink
              href={Routes.shop.index}
              className="underline color-blue-hover"
            >
              shop
            </FoundryLink>{" "}
            <span className="opacity-50">→ {product.name}</span>
          </span>
          <div className="pt-8 grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-5">
            <div
              className={`mr-0 2xl:mr-6 pb-10 md:pb-0 col-span-1 2xl:col-span-3 ${embla}`}
            >
              <div className="w-full relative">
                {!isMasonryMode && product.images.length > 1 && (
                  <>
                    <div
                      onClick={scrollPrev}
                      className="mx-1 px-6 absolute top-0 left-0 z-10 hidden md:flex w-3/12 h-full cursor-pointer"
                    />
                    <div
                      onClick={scrollNext}
                      className="mx-1 px-6 absolute top-0 right-0 z-10 hidden md:flex w-3/12 h-full cursor-pointer"
                    />
                  </>
                )}
                <div className="embla__viewport" ref={emblaRef}>
                  <div
                    className={`z-[110] grid-cols-1 2xl:grid-cols-2 gap-3 embla__container ${emblaContainer} relative`}
                  >
                    {isMasonryMode && (
                      <ResponsiveMasonry
                        columnsCountBreakPoints={{ 1: 1, 1400: 2 }}
                        className="hidden md:block"
                      >
                        <Masonry gutter="0.75rem">{imgSection}</Masonry>
                      </ResponsiveMasonry>
                    )}
                    {product.images.map((image) => {
                      return (
                        <div
                          className={`${emblaSlide} flex ${isMasonryMode
                            ? "md:hidden"
                            : product.images.length > 1
                              ? "max-w-3xl"
                              : "justify-center items-center"
                            }
                      }`}
                        >
                          <ResponsiveImage
                            src={image.fileUrl}
                            alt={image.description}
                            sizes="(max-width: 768px) 90vw, (max-width: 1536px) 40vw, 500px"
                            roundedBorder={0}
                            containerClassNames={
                              isMasonryMode && product.images.length > 1
                                ? ""
                                : "max-w-3xl"
                            }
                          ></ResponsiveImage>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div
                className={`embla__preview w-full flex flex-col md:items-center gap-4 mt-2 overflow-y-hidden ${isMasonryMode ? "md:hidden" : ""
                  }`}
              >
                <div className="flex gap-4 overflow-y-hidden z-[110]">
                  {product.images.map((image, idx) => {
                    return (
                      <div
                        className="w-16 h-16 cursor-pointer flex items-center"
                        onClick={() => {
                          emblaApi?.scrollTo(idx);
                        }}
                      >
                        <ResponsiveImage
                          src={image.fileUrl}
                          alt={image.description}
                          sizes="60px"
                          roundedBorder={0}
                          containerClassNames="relative"
                          styles={{ objectFit: "cover !important" }}
                        ></ResponsiveImage>
                      </div>
                    );
                  })}
                </div>
                {product.images.length > 1 && (
                  <div className="flex gap-4 items-center hidden md:block">
                    <Button
                      onClick={scrollPrev}
                      classes="mx-1 px-6 bg-neutral-500-hover"
                    >
                      ←
                    </Button>
                    <Button
                      onClick={scrollNext}
                      classes="mx-1 px-6 bg-neutral-500-hover"
                    >
                      →
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div className="md:pl-12 col-span-1 2xl:col-span-2 flex flex-col gap-6 max-w-3xl sticky top-20 self-start">
              <section className="flex flex-col justify-between gap-2">
                <h1 className="t2">{product.name}</h1>
                <h2 className="t4">{formatMoney("USD", product.priceCents)}</h2>
              </section>
              <section>
                <div
                  className="t6 mb-8"
                  dangerouslySetInnerHTML={{
                    __html: product.longDescriptionHTML,
                  }}
                />
                {availableQuantity > 0 && availableQuantity <= 5 && (
                  <p className="text-lg mb-8">
                    Only {availableQuantity} available.
                  </p>
                )}
                {availableQuantity > 0 ? (
                  <>
                    {quantitySelector}
                    <AddToCartButton
                      extraClasses="mt-4"
                      onClick={() => {
                        const newState = getUpdatedPhysicalProducts(
                          cartDetails?.physicalProducts,
                          product.id,
                          { quantityChange: quantity }
                        );

                        const newProductState = newState.find(
                          (p) => p.contentfulId === product.id
                        );

                        if (newProductState) {
                          const error =
                            CartDetails.getPhysicalProductErrorHelper(
                              product,
                              newProductState
                            );

                          if (error) {
                            toast.error(error.message);
                            return false;
                          }
                        }
                        savePhysicalProducts(newState);
                        propagateCartDetails();
                        return true;
                      }}
                    />
                  </>
                ) : (
                  <span className="mt-2 px-4 py-3 rounded text-sm bg-pink">
                    Sold out
                  </span>
                )}
              </section>
              <section className="mt-4">
                <hr className="w-full border-primary"></hr>
                {product.technicalDetailsHTML && (
                  <>
                    <details open>
                      <summary className="t5 color-fedblue-hover flex items-center">
                        <Info />
                        <span className="ml-3">technical details</span>
                        <span className="icon">+</span>
                      </summary>
                      <div
                        className="t6 mt-4 mb-6"
                        dangerouslySetInnerHTML={{
                          __html: product.technicalDetailsHTML,
                        }}
                      />
                    </details>
                    <hr className="w-full border-primary"></hr>
                  </>
                )}
                <details>
                  <summary className="t5 color-fedblue-hover flex items-center">
                    <ArrowRightO />
                    <span className="ml-3">shipping</span>
                    <span className="icon">+</span>
                  </summary>
                  <p className="text-lg pb-1">
                    Shipping is available within the United States. We ship
                    orders twice a week on Mondays and Thursdays; please allow
                    for 1-2 weeks of shipping time depending on your location.
                  </p>
                  <p className="text-lg pb-1">
                    See our{" "}
                    <FoundryLink
                      href={Routes.faq}
                      className="underline underline-offset-4 color-blue-hover"
                    >
                      shipping and return policies
                    </FoundryLink>{" "}
                    for more information.
                  </p>
                </details>
                <hr className="w-full border-primary"></hr>
                <details>
                  <summary className="t5 color-fedblue-hover flex items-center">
                    <Heart />
                    <span className="ml-3">care information</span>
                    <span className="icon">+</span>
                  </summary>

                  {product.careInformationHTML ? (
                    <div
                      className="t6 mb-6"
                      dangerouslySetInnerHTML={{
                        __html: product.careInformationHTML,
                      }}
                    />
                  ) : (
                    <p className="text-lg pb-1">
                      Keep out of direct sunlight to avoid fading and handle
                      with care. Riso ink never fully dries, and natural oils
                      may create slight smudging.
                    </p>
                  )}
                </details>
                <hr className="w-full border-primary"></hr>
              </section>
            </div>
          </div>
          <section className="w-full">
            <h1 className="t2 flex items-center justify-center mt-36 mb-24">
              <span>
                <span className="color-mint">✿</span>
                <span>
                  {product.relatedProducts.length > 0
                    ? " Related Products "
                    : " Discover more "}
                </span>
                <span className="color-mint">✿</span>
              </span>
            </h1>
            <div className="mt-12 container-padded grid grid-cols-2 lg:grid-cols-3 gap-12 md:gap-24">
              {(product.relatedProducts.length > 0
                ? product.relatedProducts
                : otherProducts
              ).map((p) => (
                <ProductCard product={p} hideButton={true}></ProductCard>
              ))}
            </div>
          </section>
        </div>
      </PageFadeWrapper>
    </>
  );
}

Product.displayName = "ShopProduct";
export default Product;
